import React, { useContext, useEffect, useRef, useState } from 'react';

import { styled } from '@mui/material/styles';
import {
    PageToolbar,
    templateCoreActions,
    withTemplateCore,
    editionLevels,
} from '@sunwisesoftware/racking-template-core';
import { Box } from '@sunwisesoftware/sunwise-ui';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { compose } from 'recompose';
import { createStructuredSelector } from 'reselect';

import SwipeableDrawer from 'common/components/SwipeableDrawer';
import { useBreakpoint } from 'common/hooks';
import { LoadingContext } from 'common/utils/contexts';
import { custom1 } from 'common/utils/customScrolls';
import { getCurrentLanguage } from 'common/utils/helpers/multiregion';
import { getToken } from 'common/utils/helpers/session';

import * as actions from './actions';
import {
    HeaderPreview,
    PreviewContent,
    SupportMenuContent,
} from './components';
import { PDF_MEDIUM_QUALITY } from './constants';
import {
    getIsDownloadingFileContextMessage,
    getIsLoadingContextMessage,
    getIsUpdatingContextMessage,
    handleClickDocumentItemBuild,
    handleClickDownloadBuild,
    handleCloseDrawer,
} from './helpers';
import * as selectors from './selectors';

const PagesContainer = styled(Box)(
    ({ theme }) => `
        background: ${theme.palette.mode === 'dark' ? '#1b1c1f' : '#f2f4f9'};
        display: flex;
        flex-direction: column;
        overflow: auto;
        position: relative;
        ${custom1}
`,
);

const PageWrapper = styled(Box)`
    display: flex;
    flex-direction: column;
    width: 100%;
`;

const MenuWrapper = styled(Box)`
    display: flex;
    flex-direction: column;
    overflow-y: scroll;
    ${custom1}
`;

const Container = ({
    attachments,
    boms,
    datasheets,
    downloadDocumentPdf,
    downloadProposalPdf,
    downloadProposalXls,
    fetchProposalContent,
    initialize,
    initTemplate,
    isDownloading,
    isFetchingProposalContent,
    isFetchingProposalDetails,
    isInitializing,
    isUpdatingCustomTemplate,
    match,
    proposalDetails,
    reset,
    resetTemplate,
    segmentsExtraData,
    selectedDocument,
    setSelectedDocument,
    systems,
}) => {
    const loadingContext = useContext(LoadingContext);
    // Always in view mode — only the 3D widget's own Save button triggers saves.
    const editionDisabled = true;
    const [headerHeight, setHeaderHeight] = useState(0);
    const [downloadFormat, setDownloadFormat] = useState(
        parseInt(localStorage.getItem('downloadFormat')) || 2,
    );
    const headerRef = useRef(null);
    const sideBarRef = useRef(null);
    const swipeableDrawerRef = useRef(null);
    const breakpoint = useBreakpoint();
    const proposalId = match.params.uid;

    useEffect(() => {
        getIsLoadingContextMessage({
            isFetchingProposalDetails,
            isFetchingProposalContent,
            isInitializing,
            loadingContext,
        });
    }, [isFetchingProposalDetails, isFetchingProposalContent, isInitializing]);

    useEffect(() => () => reset(), []);

    useEffect(() => {
        if (proposalId) {
            resetTemplate();
            initialize(initTemplate, proposalId, (data) =>
                setSelectedDocument(data),
            );
        }
    }, [proposalId]);

    useEffect(() => {
        getIsDownloadingFileContextMessage(isDownloading, loadingContext);
    }, [isDownloading]);

    useEffect(() => {
        getIsUpdatingContextMessage(
            true,
            isUpdatingCustomTemplate,
            loadingContext,
            () => {},
        );
    }, [isUpdatingCustomTemplate]);

    useEffect(() => {
        if (!headerRef.current) return;
        const resizeObserver = new ResizeObserver(() => {
            setHeaderHeight(headerRef?.current?.clientHeight);
        });
        resizeObserver.observe(headerRef.current);
        return () => resizeObserver.disconnect(); // clean up
    }, []);

    useEffect(() => {
        if (headerRef?.current)
            setHeaderHeight(headerRef?.current?.clientHeight);
    }, [breakpoint]);

    const handleClickDatasheetItem = (datasheet) => {
        resetTemplate();
        setSelectedDocument(datasheet);
        setDownloadFormat(PDF_MEDIUM_QUALITY);
        handleCloseDrawer(swipeableDrawerRef);
    };

    const handleClickDownload = () =>
        handleClickDownloadBuild(
            downloadDocumentPdf,
            downloadProposalPdf,
            downloadProposalXls,
            downloadFormat,
            loadingContext,
            selectedDocument,
        );

    const handleClickDocumentItem = (document) =>
        handleClickDocumentItemBuild(
            document,
            fetchProposalContent,
            initTemplate,
            resetTemplate,
            selectedDocument,
            setDownloadFormat,
            setSelectedDocument,
            swipeableDrawerRef,
        );

    return (
        <>
            <Box sx={{ display: 'flex', height: '100%', width: '100%' }}>
                <Box sx={{ width: { md: 'calc(100% - 400px)', xs: '100%' } }}>
                    <HeaderPreview
                        headerRef={headerRef}
                        proposalDetails={proposalDetails}
                    />

                    <PagesContainer
                        sx={{
                            height: {
                                md: 'calc(100vh - 130px)',
                                xs: `calc(100vh - ${headerHeight + 52}px)`,
                            },
                        }}
                    >
                        <Box
                            sx={{
                                alignItems: 'center',
                                display: 'flex',
                                flexDirection: 'column',
                            }}
                        >
                            <PageToolbar
                                editionLevel={
                                    editionLevels.PARTIAL_EDITION_MODE
                                }
                                sxToolbar={{ marginLeft: '-8px' }}
                            />
                            <PageWrapper
                                sx={{
                                    height: {
                                        md: 'calc(100vh - 167px)',
                                        xs: `calc(100vh - ${headerHeight + 137}px)`,
                                    },
                                    pt: 2,
                                    '& > div > div': {
                                        display: 'block',
                                        margin: '0 auto 26px auto',
                                    },
                                }}
                            >
                                <PreviewContent
                                    selectedDocument={selectedDocument}
                                    editionDisabled={editionDisabled}
                                />
                            </PageWrapper>
                        </Box>
                    </PagesContainer>
                </Box>

                <MenuWrapper
                    ref={sideBarRef}
                    sx={{
                        display: { md: 'flex', xs: 'none' },
                        height: '100vh',
                        p: 2,
                        width: 400,
                        flexShrink: 0,
                    }}
                >
                    <SupportMenuContent
                        attachments={attachments}
                        boms={boms}
                        datasheets={datasheets}
                        downloadFormat={downloadFormat}
                        editionDisabled={editionDisabled}
                        handleClickDatasheetItem={handleClickDatasheetItem}
                        handleClickDocumentItem={handleClickDocumentItem}
                        handleClickDownload={handleClickDownload}
                        isUpdatingCustomTemplate={isUpdatingCustomTemplate}
                        proposalDetails={proposalDetails}
                        selectedDocument={selectedDocument}
                        setDownloadFormat={setDownloadFormat}
                        swipeableDrawerRef={swipeableDrawerRef}
                        systems={systems}
                        segmentsExtraData={segmentsExtraData}
                    />
                </MenuWrapper>
            </Box>

            <SwipeableDrawer ref={swipeableDrawerRef}>
                <SupportMenuContent
                    attachments={attachments}
                    boms={boms}
                    datasheets={datasheets}
                    downloadFormat={downloadFormat}
                    editionDisabled={editionDisabled}
                    handleClickDatasheetItem={handleClickDatasheetItem}
                    handleClickDocumentItem={handleClickDocumentItem}
                    handleClickDownload={handleClickDownload}
                    isUpdatingCustomTemplate={isUpdatingCustomTemplate}
                    proposalDetails={proposalDetails}
                    selectedDocument={selectedDocument}
                    setDownloadFormat={setDownloadFormat}
                    swipeableDrawerRef={swipeableDrawerRef}
                    systems={systems}
                    segmentsExtraData={segmentsExtraData}
                />
            </SwipeableDrawer>
        </>
    );
};

Container.propTypes = {
    attachments: PropTypes.array,
    boms: PropTypes.array,
    datasheets: PropTypes.array,
    downloadDocumentPdf: PropTypes.func,
    downloadProposalPdf: PropTypes.func,
    downloadProposalXls: PropTypes.func,
    fetchProposalContent: PropTypes.func,
    initialize: PropTypes.func,
    initTemplate: PropTypes.func,
    isDownloading: PropTypes.bool,
    isFetchingProposalContent: PropTypes.bool,
    isFetchingProposalDetails: PropTypes.bool,
    isInitializing: PropTypes.bool,
    isUpdatingCustomTemplate: PropTypes.bool,
    match: PropTypes.object,
    proposalDetails: PropTypes.object,
    reset: PropTypes.func,
    resetTemplate: PropTypes.func,
    segmentsExtraData: PropTypes.array,
    selectedDocument: PropTypes.object,
    setSelectedDocument: PropTypes.func,
    systems: PropTypes.array,
};

const mapStateToProps = createStructuredSelector({
    attachments: selectors.getAttachments,
    boms: selectors.getBoms,
    datasheets: selectors.getDatasheets,
    isDownloading: selectors.getIsDownloading,
    isFetchingProposalContent: selectors.getIsFetchingProposalContent,
    isFetchingProposalDetails: selectors.getIsFetchingProposalDetails,
    isInitializing: selectors.getIsInitializing,
    isUpdatingCustomTemplate: selectors.getIsUpdatingCustomTemplate,
    proposalContentData: selectors.getProposalContentData,
    proposalDetails: selectors.getProposalDetailsData,
    segmentsExtraData: selectors.getSegmentsExtraData,
    selectedDocument: selectors.getSelectedDocument,
    systems: selectors.getSystems,
});

const mapDispatchToProps = (dispatch) => ({
    dispatchWidgetStyleUpdate: (widgetId, style) =>
        dispatch(templateCoreActions.CHANGE_WIDGET_STYLE({ style, widgetId })),
    downloadDocumentPdf: (archive, loadingContext, name) =>
        dispatch(actions.downloadDocumentPdf(archive, loadingContext, name)),
    downloadProposalPdf: (contentId, downloadFormat, name) =>
        dispatch(actions.downloadProposalPdf(contentId, downloadFormat, name)),
    downloadProposalXls: (contentId, name) =>
        dispatch(actions.downloadProposalXls(contentId, name)),
    fetchProposalContent: (contentId, initTemplate) =>
        dispatch(actions.fetchProposalContent(contentId, initTemplate)),
    initialize: (initTemplate, proposalId, callback) =>
        dispatch(actions.initialize(initTemplate, proposalId, callback)),
    reset: () => dispatch(actions.reset()),
    setSelectedDocument: (values) =>
        dispatch(actions.setSelectedDocument(values)),
    updateTemplate: (contentId, getTemplateUpdatedData, setTemplateBackup) =>
        dispatch(
            actions.updateTemplate(
                contentId,
                getTemplateUpdatedData,
                setTemplateBackup,
            ),
        ),
});

export default compose(
    connect(mapStateToProps, mapDispatchToProps),
    withRouter,
    withTemplateCore((props) => ({
        baseUrl: import.meta.env.VITE_API_URL,
        editionMode: editionLevels.PARTIAL_EDITION_MODE,
        froalaLicenseKey: import.meta.env.VITE_FROALA_LICENSE,
        googleApiKey: import.meta.env.VITE_GOOGLE_MAPS_KEY,
        language: getCurrentLanguage(),
        onCameraUpdate: (widgetField, widgetOrmId, cameraStyle) => {
            const contentId = props.selectedDocument?.contentId;
            const originalContent = props.proposalContentData;
            if (!contentId || !originalContent || !widgetField) return;

            // Extract pageId from ORM id (format: "${pageId}/${field}").
            // This limits the update to the exact page where the widget lives,
            // preventing the same field name on other system pages from being
            // overwritten.
            const pageId = widgetOrmId?.includes('/')
                ? widgetOrmId.substring(0, widgetOrmId.lastIndexOf('/'))
                : null;

            const updatedPagesTemplate = originalContent.pages_template.map(
                (page) => {
                    // Skip pages that don't match — same field can exist on
                    // sibling pages (one per system).
                    if (pageId && page.id !== pageId) return page;

                    let pageObj;
                    try {
                        pageObj = JSON.parse(page.content);
                    } catch {
                        return page;
                    }
                    const pageKey = Object.keys(pageObj)[0];
                    if (!pageKey) return page;
                    const pageData = pageObj[pageKey];
                    const widgets = pageData?.widgets ?? [];
                    if (!widgets.some((w) => w.field === widgetField))
                        return page;
                    return {
                        ...page,
                        content: JSON.stringify({
                            [pageKey]: {
                                ...pageData,
                                widgets: widgets.map((w) =>
                                    w.field === widgetField
                                        ? {
                                              ...w,
                                              meta: {
                                                  ...w.meta,
                                                  style: {
                                                      ...w.meta?.style,
                                                      ...cameraStyle,
                                                  },
                                              },
                                          }
                                        : w,
                                ),
                            },
                        }),
                    };
                },
            );

            // Update the ORM state immediately so the local snapshot is
            // available for print/preview without needing a full refetch.
            if (widgetOrmId) {
                props.dispatchWidgetStyleUpdate(widgetOrmId, cameraStyle);
            }

            props.updateTemplate(
                contentId,
                () => ({
                    ...originalContent,
                    pages_template: updatedPagesTemplate,
                }),
                () => {},
            );
        },
        token: getToken(),
    })),
)(Container);
